<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div class="layout--main" :class="[layoutTypeClass, navbarClasses, footerClasses, {'no-scroll': isAppPage}]">
		<vx-tour
			:steps="steps"
			v-if="
				!disableThemeTour &&
				windowWidth >= 1200 &&
				mainLayoutType === 'vertical' &&
				verticalNavMenuWidth == 'default'
			"
		/>

		<the-customizer
			v-if="!disableCustomizer"
			:footerType="footerType"
			:hideScrollToTop="hideScrollToTop"
			:navbarType="navbarType"
			:navbarColor="navbarColor"
			:routerTransition="routerTransition"
			@toggleHideScrollToTop="toggleHideScrollToTop"
			@updateFooter="updateFooter"
			@updateNavbar="updateNavbar"
			@updateNavbarColor="updateNavbarColor"
			@updateRouterTransition="updateRouterTransition"
		/>

		<v-nav-menu
			title="utero"
			parent=".layout--main"
			:navMenuItems="navMenuItemsFilter"
			:navMenuColor="navbarColor"
		/>

		<div id="content-area" :class="[contentAreaClass, {'show-overlay': bodyOverlay}]">
			<div id="content-overlay" />
			<!-- Navbar -->
			<template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
				<the-navbar-horizontal
					:navbarType="navbarType"
					:class="[{'text-white': isNavbarDark && !isThemeDark}, {'text-base': !isNavbarDark && isThemeDark}]"
				/>
				<div style="height: 62px" v-if="navbarType === 'static'"></div>
				<h-nav-menu
					:class="[{'text-white': isNavbarDark && !isThemeDark}, {'text-base': !isNavbarDark && isThemeDark}]"
					:navMenuItems="navMenuItemsFilter"
				/>
			</template>
			<template v-else>
				<the-navbar-vertical
					:navbarColor="'#fff'"
					:class="[{'text-white': isNavbarDark && !isThemeDark}, {'text-base': !isNavbarDark && isThemeDark}]"
				/>
			</template>
			<!-- /Navbar -->
			<div class="content-wrapper">
				<div class="router-view">
					<div class="router-content">
						<transition :name="routerTransition">
							<div
								v-if="$route.meta.breadcrumb || $route.meta.pageTitle"
								class="router-header flex flex-wrap items-center mb-6"
							>
								<div
									class="content-area__heading"
									:class="{
										'pr-4 border-0 md:border-r border-solid border-grey-light':
											$route.meta.breadcrumb,
									}"
								>
									<h2 class="mb-1">{{ $t(routeTitle) }}</h2>
								</div>
							</div>
						</transition>
						<div class="content-area__content">
							<back-to-top
								bottom="5%"
								:right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'"
								visibleoffset="500"
								v-if="!hideScrollToTop"
							>
								<vs-button icon-pack="feather" icon="icon-arrow-up" class="shadow-lg btn-back-to-top" />
							</back-to-top>
							<transition :name="routerTransition" mode="out-in">
								<router-view
									@changeRouteTitle="changeRouteTitle"
									@setAppClasses="(classesStr) => $emit('setAppClasses', classesStr)"
								/>
							</transition>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BackToTop from "vue-backtotop";
import HNavMenu from "@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue";
import navMenuItems from "@/layouts/components/vertical-nav-menu/navMenuItems.js";
import TheCustomizer from "@/layouts/components/customizer/TheCustomizer.vue";
import TheNavbarHorizontal from "@/layouts/components/navbar/TheNavbarHorizontal.vue";
import TheNavbarVertical from "@/layouts/components/navbar/TheNavbarVertical.vue";
import TheFooter from "@/layouts/components/TheFooter.vue";
import themeConfig from "@/../themeConfig.js";
import VNavMenu from "@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";
import User from "@/models/User.js";
import {firebaseInstance, messaging} from "../../main";
import {downloadFileFromFirebase} from "@/helpers/firebaseDB.js";

const VxTour = () => import("@/components/VxTour.vue");
export default {
	components: {
		BackToTop,
		HNavMenu,
		TheCustomizer,
		TheFooter,
		TheNavbarHorizontal,
		TheNavbarVertical,
		VNavMenu,
		VxTour,
	},
	data() {
		return {
			disableCustomizer: themeConfig.disableCustomizer,
			disableThemeTour: themeConfig.disableThemeTour,
			dynamicWatchers: {},
			footerType: themeConfig.footerType || "static",
			hideScrollToTop: themeConfig.hideScrollToTop,
			isNavbarDark: false,
			navbarType: themeConfig.navbarType || "floating",
			routerTransition: themeConfig.routerTransition || "none",
			routeTitle: this.$route.meta.i18n,
			steps: [
				{
					target: "#btnVNavMenuMinToggler",
					content: "Toggle Collapse Sidebar.",
				},
				{
					target: ".vx-navbar__starred-pages",
					content: "Create your own bookmarks. You can also re-arrange them using drag & drop.",
				},
				{
					target: ".i18n-locale",
					content: "You can change language from here.",
				},
				{
					target: ".navbar-fuzzy-search",
					content: "Try fuzzy search to visit pages in flash.",
				},
				{
					target: ".customizer-btn",
					content: "Customize template based on your preference",
					params: {
						placement: "left",
					},
				},
				{
					target: ".vs-button.buy-now",
					content: "Buy this awesomeness at affordable price!",
					params: {
						placement: "top",
					},
				},
			],
			userSession: new User(),
			permissions: [],
			role: "",
			resourcesId: [],
			messages: [],
		};
	},
	watch: {
		$route() {
			this.routeTitle = this.$route.meta.pageTitle;
		},
		isThemeDark(val) {
			const color = this.navbarColor === "#fff" && val ? "#10163a" : "#fff";
			this.updateNavbarColor(color);
		},
		"$store.state.mainLayoutType"(val) {
			this.setNavMenuVisibility(val);
			this.disableThemeTour = true;
		},
	},
	computed: {
		// traslate de title on object route
		dataRoute() {
			let original = this.$route;
			let traslateRoute = this.$route.meta.breadcrumb.map((t) => {
				t.title = this.$t(t.i18n);
				return t;
			});
			original.meta.breadcrumb = traslateRoute;
			return original;
		},
		bodyOverlay() {
			return this.$store.state.bodyOverlay;
		},
		contentAreaClass() {
			if (this.mainLayoutType === "vertical") {
				if (this.verticalNavMenuWidth === "default") return "content-area-reduced";
				else if (this.verticalNavMenuWidth === "reduced") return "content-area-lg";
				else return "content-area-full";
			} else return "content-area-full";
		},
		footerClasses() {
			return {
				"footer-hidden": this.footerType === "hidden",
				"footer-sticky": this.footerType === "sticky",
				"footer-static": this.footerType === "static",
			};
		},
		isAppPage() {
			return this.$route.meta.no_scroll;
		},
		isThemeDark() {
			return this.$store.state.theme === "dark";
		},
		layoutTypeClass() {
			return `main-${this.mainLayoutType}`;
		},
		mainLayoutType() {
			return this.$store.state.mainLayoutType;
		},
		navbarClasses() {
			return {
				"navbar-hidden": this.navbarType === "hidden",
				"navbar-sticky": this.navbarType === "sticky",
				"navbar-static": this.navbarType === "static",
				"navbar-floating": this.navbarType === "floating",
			};
		},
		verticalNavMenuWidth() {
			return this.$store.state.verticalNavMenuWidth;
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		navbarColor: {
			get() {
				return this.$store.state.moduleSetting.navbarColor;
			},
			set(val) {
				this.$store.dispatch("moduleSetting/changeNavbarColor", val);
			},
		},
		navMenuItemsFilter() {
			const res = [...navMenuItems];
			return res;
		},
	},
	methods: {
		changeRouteTitle(title) {
			this.routeTitle = title;
		},
		updateNavbar(val) {
			if (val === "static") this.updateNavbarColor(this.isThemeDark ? "#10163a" : "#fff");
			this.navbarType = val;
		},
		updateNavbarColor(val) {
			if (val === "#fff") this.isNavbarDark = false;
			else this.isNavbarDark = true;
		},
		updateFooter(val) {
			this.footerType = val;
		},
		updateRouterTransition(val) {
			this.routerTransition = val;
		},
		setNavMenuVisibility(layoutType) {
			if (
				(layoutType === "horizontal" && this.windowWidth >= 1200) ||
				(layoutType === "vertical" && this.windowWidth < 1200)
			) {
				this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", false);
				this.$store.dispatch("updateVerticalNavMenuWidth", "no-nav-menu");
			} else {
				this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", true);
			}
		},
		toggleHideScrollToTop(val) {
			this.hideScrollToTop = val;
		},
		async getPermissions() {
			const user = await this.userSession.getUser();
			this.permissions = user.permissions;
			this.role = user.role_name;
		},
		sendMessage() {
			const data = {
				to: "fcmToken",
				data: {
					title: "username [$groupName]",
					body: "message",
					chat_room: "",
					is_group_chat: "true",
				},
			};
			fetch(`https://fcm.googleapis.com/fcm/send`, {
				method: "POST", // or 'PUT'
				body: JSON.stringify(data), // data can be `string` or {object}!
				headers: {
					Authorization: `key=${process.env.VUE_APP_FIREBASE_MESSAGING_TOKEN}`,
					"Content-Type": "application/json",
				},
			})
				.then((response) => {
					return response.json();
				})
				.then((response) => {})
				.catch((err) => console.error(err));
		},
	},
	async created() {
		const color = this.navbarColor === "#fff" && this.isThemeDark ? "#10163a" : this.navbarColor;
		this.updateNavbarColor(color);
		this.setNavMenuVisibility(this.$store.state.mainLayoutType);
		this.dynamicWatchers.windowWidth = this.$watch("$store.state.windowWidth", (val) => {
			if (val < 1200) {
				this.disableThemeTour = true;
				this.dynamicWatchers.windowWidth();
			}
		});
		this.dynamicWatchers.verticalNavMenuWidth = this.$watch("$store.state.verticalNavMenuWidth", () => {
			this.disableThemeTour = true;
			this.dynamicWatchers.verticalNavMenuWidth();
		});
		this.dynamicWatchers.rtl = this.$watch("$vs.rtl", () => {
			this.disableThemeTour = true;
			this.dynamicWatchers.rtl();
		});
		// Obtener permisos y guardarlos en el state de la app
		this.$store.dispatch("getPermissions");
		//Save and recovery the configuration for routes on indexed db
		this.$store.dispatch("moduleMap/saveConfigurationOnIndexedDB");
		this.$store.dispatch("modulePreferences/savePreferencesOnIndexedDB").then((response) => {
			this.$i18n.locale = response.language;
			this.$store.commit("UPDATE_LOCALE_APP", response.language);
		});
		// Get Data localStorage
		const userInfo = JSON.parse(localStorage.getItem("userInfo"));
		if (userInfo.datarutas_role_name === "GOTU") {
			userInfo.userRole = "admin";
			this.$acl.change("admin");
		} else {
			userInfo.userRole = "editor";
			this.$acl.change("editor");
		}
		// Store data in localStorage
		localStorage.setItem("userInfo", JSON.stringify(userInfo));
		// Request all of permissions for configList
		this.$store
			.dispatch("moduleCompanyConfig/fetchCompanyConfigList")
			.then(async (companyConfigList) => {
				await this.$store.dispatch("modulePreferences/saveCompanyConfigListPreferences", companyConfigList);
				const visualConfig = this.$store.getters["moduleCompanyConfig/visualConfig"];
				if (visualConfig && visualConfig.config_json) {
					const {navbar_color, primary_color, text, url_logo} = visualConfig.config_json;
					this.$store
						.dispatch("moduleSetting/saveColorsOnIndexedDB", {
							navbar_color,
							primary_color,
							text,
							url_logo,
						})
						.then((res) => {
							this.$vs.theme({primary: res.primaryColor});
						});
				}
			})
			.catch((error) => {
				throw new Error(error);
			});
		const resources = await this.$store.dispatch("chat/fetchContacts");
		await this.$store.dispatch("chat/fetchChats");
		resources.forEach((resource) => {
			downloadFileFromFirebase(resource.img_url)
				.then((res) => {
					this.$store.commit("chat/ADD_CONTACT_IMAGE", {
						id: resource.id,
						img: res,
					});
				})
				.catch((error) => {});
		});
	},
	async mounted() {
		Notification.requestPermission().then((permission) => {});
		this.$messaging
			.getToken({vapidKey: process.env.VUE_APP_VAPIDKEY})
			.then(async (currentToken) => {
				if (currentToken) {
					await this.$store.dispatch("chat/registerUsers", {dm: {}, groups: {}, fcm_token: currentToken});
				}
			})
			.catch((err) => {
				console.error("An error occurred while retrieving token. ", err);
			});
	},
	beforeDestroy() {
		Object.keys(this.dynamicWatchers).map((i) => {
			this.dynamicWatchers[i]();
			delete this.dynamicWatchers[i];
		});
	},
};
</script>
